import React, { useCallback, useContext, useMemo } from 'react'
import { cn } from '@appfluence/classnames'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { DetailsList, SelectionMode } from '@fluentui/react/lib/DetailsList'
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator'
import { Link } from '@fluentui/react/lib/Link'
import { itemHelper, linkDependencyHelper, stateHelper, URLHelper } from '../../common/src/helpers'
import { externalLinkHandler } from '../../utils/externalLinkHandler'
import { useMergeState } from '../../common/src/hooks/enhancedHooks'
import { ActionButton } from '@fluentui/react/lib/Button'
import { deleteLink } from '../../common/src/actions/linksAPI'
import { linkSubject, SOURCES } from '../../reactions/linkSubject'
import { AlertModal } from '../../components/modal/AlertModal'
import { useCopyColumn } from '../../components/item/useCopyColumn'
import { useTranslation } from 'react-i18next'
import { DateForResource } from './FilesSectionContent'
import { ItemLinkPanelContext } from '../../contexts'
import { LINK_PANEL_ACTION_TYPES } from '../../components/panels/item/ItemLinkPanelSingleton'
import { rowStyles, ResourcesSectionCard, Placeholder, SectionHeader, SectionTitle } from './resourcesSectionCommon'
import { TooltipHost } from '@/components/tooltip/TooltipHost'
import { Toolbar, ToolbarButton } from '@fluentui/react-components'
import { DocumentTextLink, Link as LinkIcon } from '../../components/BundledIcons'

const LinksSectionCard = styled(ResourcesSectionCard)`
  & .actionButtonCell {
    padding: 0 4px;
    &:first-child {
      padding-left: 8px;
    }
  }
`

const Extra = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 10px;
`

const _style = {
  linkIconCell: {
    textAlign: 'left',
    fontSize: '16px',
  },
  linkNameCell: {
    textAlign: 'center',
  },
  columnIcon: {
    verticalAlign: 'middle',
    maxHeight: '12px',
    maxWidth: '12px',
  },
  linkIconAction: {
    fontSize: '12px',
  },
  label: {
    margin: '5px',
  },
  cell: {
    fontSize: '16px',
  },
}

const StyledLink = styled(Link).attrs({
  target: '_blank',
  rel: 'noopener noreferrer',
  styles: {
    root: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
})`
  &.done {
    text-decoration: line-through;
  }
`

const TypeString = styled.div`
  color: ${p => p.theme.palette.neutralDark};
  font-style: italic;
`

const iconStyles = {
  root: {
    fontSize: 14,
  },
}

const actionButtonStyles = {
  root: {
    padding: 0,
    height: 32,
  },
}

export const LinksSectionContent = ({
  item,
  readOnly = false,
  selectedLink,
  setSelectedLink,
  loading: ownLoading,
  setLoading,
  setShowLinkModalToEdit,
  linksItems,
}) => {
  const [state, setState] = useMergeState({
    showItemPanelToEdit: false,
    showAlertModalToDelete: false,
    lastSelectedDependency: 0,
  })
  const itemId = itemHelper.getId(item)
  const links = useSelector(state => stateHelper.getAssociatedLinksForItem(state, itemId))
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { showAlertModalToDelete } = state
  const { dispatch: itemLinkDispatch, loading: itemLinkPanelLoading } = useContext(ItemLinkPanelContext)
  const loading = ownLoading || itemLinkPanelLoading
  const copyColumn = useCopyColumn()

  const onClickAddLink = useCallback(() => {
    setShowLinkModalToEdit(true)
    setSelectedLink({})
  }, [setShowLinkModalToEdit, setSelectedLink])

  const onClickAddItem = useCallback(() => {
    itemLinkDispatch({ type: LINK_PANEL_ACTION_TYPES.SHOW_ADD_LINK, payload: { itemId } })
  }, [itemId, itemLinkDispatch])

  const wantToDeleteSelectedLink = useCallback(
    link => {
      setState({ showAlertModalToDelete: true })
      setSelectedLink(link)
    },
    [setState, setSelectedLink]
  )

  const deleteSelectedLink = useCallback(async () => {
    setLoading(true)
    await dispatch(deleteLink(itemId, selectedLink.id))
    setLoading(false)
  }, [setLoading, dispatch, itemId, selectedLink.id])

  const onClickLink = link => {
    if (link.linked_item) {
      const r = URLHelper.resourceURIParser(link.linked_item)
      if (r && r.id) {
        linkSubject.next({ urlData: r, source: SOURCES.ITEM_RESOURCES })
        return true
      }
    }
    return externalLinkHandler(link.link)
  }

  const columns = [
    {
      key: 'columnDelete',
      name: t('links_section.delete_column'),
      isIconOnly: true,
      minWidth: 12,
      maxWidth: 12,
      className: 'actionButtonCell',
      onRender: link => {
        const onClickDelete = () => wantToDeleteSelectedLink(link)
        return (
          <TooltipHost content={t('item_detail.delete_link')}>
            <ActionButton
              onClick={onClickDelete}
              styles={actionButtonStyles}
              iconProps={{ iconName: 'Delete', styles: iconStyles }}
            ></ActionButton>
          </TooltipHost>
        )
      },
    },
    {
      key: 'columnEdit',
      name: t('links_section.edit_column'),
      isIconOnly: true,
      minWidth: 12,
      maxWidth: 12,
      className: 'actionButtonCell',
      onRender: link => {
        const onClickEdit = () => {
          if (link.linked_item) {
            itemLinkDispatch({
              type: LINK_PANEL_ACTION_TYPES.SHOW_EDIT_DEPENDENCY,
              payload: { itemId, linkedItem: link },
            })
          } else {
            setShowLinkModalToEdit(true)
            setSelectedLink(link)
          }
        }
        return (
          <TooltipHost content={t('item_detail.edit_link')}>
            <ActionButton
              height={8}
              width={8}
              onClick={onClickEdit}
              iconProps={{ iconName: 'Edit', styles: iconStyles }}
              styles={actionButtonStyles}
            ></ActionButton>
          </TooltipHost>
        )
      },
    },
    {
      key: 'columnType',
      name: t('links_section.type_column'),
      className: _style.linkIconCell,
      isIconOnly: false,
      fieldName: 'name',
      minWidth: 50,
      maxWidth: 60,
      onRender: link => {
        const dependencyKind = link.linked_item ? linkDependencyHelper.getDependencyKind(link.dependency) : 'Link'
        return <TypeString>{dependencyKind}</TypeString>
      },
    },
    {
      key: 'columnLink',
      name: t('links_section.name_column'),
      className: _style.linkNameCell,
      isIconOnly: false,
      minWidth: 16,
      onColumnClick: null,
      onRender: link => {
        const onClick = evt => {
          const handled = onClickLink(link)
          if (handled) {
            evt.preventDefault()
          }
        }
        const completion = link?.linked_item_details?.completionPercentage
        const completionCN = cn({ pm_hidden: !completion })
        const linkCN = cn({ done: completion === 100 })
        return (
          <>
            <StyledLink href={link.link} onClick={onClick} title={link.name} className={linkCN}>
              {link.name}
            </StyledLink>
            <Extra>
              <DateForResource timestamp={link.timestamp} />
              <span className={completionCN}>&nbsp;-&nbsp;({completion}%)</span>
            </Extra>
          </>
        )
      },
    },
    copyColumn,
  ]

  const alertModalSubText = useMemo(() => {
    const name = selectedLink.name || ''
    if (selectedLink.linked_item) {
      return t('links_section.delete_confirmation_linked_item', { name })
    }
    return t('links_section.delete_confirmation_linked_item', { name })
  }, [selectedLink, t])

  const placeholder = !readOnly && <Placeholder>{t('links_section.placeholder')}</Placeholder>

  const onRenderRow = (props, defaultRender) => {
    props.styles = rowStyles
    return defaultRender(props)
  }

  return (
    <LinksSectionCard>
      <SectionHeader>
        <SectionTitle>{t('item_detail.links_section_title')}</SectionTitle>
        {!readOnly && (
          <Toolbar>
            <ToolbarButton disabled={loading} onClick={onClickAddItem} icon={<DocumentTextLink />}>
              {t('links_section.add_item')}
            </ToolbarButton>
            <ToolbarButton disabled={loading} onClick={onClickAddLink} icon={<LinkIcon />}>
              {t('links_section.add_link')}
            </ToolbarButton>
          </Toolbar>
        )}
        {/*{!readOnly && <CommandBar farItems={commandItems} styles={{ root: { padding: '0px' } }} />}*/}
      </SectionHeader>
      {loading && <ProgressIndicator />}
      {links.size > 0 ? (
        <DetailsList
          setKey={'links'}
          items={linksItems}
          columns={columns}
          selectionMode={SelectionMode.none}
          onRenderRow={onRenderRow}
        />
      ) : (
        placeholder
      )}
      <AlertModal
        title={t('links_section.delete_confirmation_title')}
        subText={alertModalSubText}
        open={showAlertModalToDelete}
        onDismiss={() => {
          setState({ showAlertModalToDelete: false })
          setSelectedLink({})
        }}
        primaryActionText={t('general.delete')}
        onPrimaryActionClick={deleteSelectedLink}
      />
    </LinksSectionCard>
  )
}
